import Page from "../Page/Page";
import APIKeys from "./APIKeys";
import { clearApplicationState } from "@app/state/cleanup";
import { useUserStore } from "@app/state/store";
import { useAuth0 } from "@auth0/auth0-react";
import Icon from "@lib/common/components/Icon/Icon";
import { getRoleColor } from "@lib/iam/utils/getRoleColor";
import { getRoleDisplayName } from "@lib/iam/utils/getRoleDisplayName";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Center,
  Container,
  CopyButton,
  Divider,
  Group,
  Stack,
  Text,
  Title,
  Tooltip
} from "@mantine/core";

export default function UserProfile() {
  const { isLoading, isAuthenticated, error, user: auth0User, loginWithRedirect, logout } = useAuth0();
  const user = useUserStore();

  if (error) {
    return (
      <Container>
        <Center mt="20vh">
          <Stack>
            <Title>Error</Title>
            <Text>{error.message}</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  if (isLoading || !user) {
    return (
      <Container>
        <Center mt="20vh">
          <Text>Loading...</Text>
        </Center>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container>
        <Center mt="20vh">
          <Stack>
            <Title>Not Authenticated</Title>
            <Button onClick={() => loginWithRedirect()}>Log In</Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Page headerLeft={<Page.HeaderTitle>Profile</Page.HeaderTitle>}>
      <Container size="sm">
        <Center mt="10vh">
          <Stack gap="xl" style={{ width: "100%", maxWidth: 400 }}>
            <Card shadow="sm" padding="lg" withBorder>
              <Stack gap="md">
                <Center>
                  <Avatar src={auth0User?.picture} size={100} radius="xl" alt={user?.name || "User avatar"} />
                </Center>

                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text fw={500}>Name:</Text>
                    <Text>{user?.name}</Text>
                  </Group>

                  <Group justify="space-between">
                    <Text fw={500}>ID:</Text>
                    <Text>{user?.id}</Text>
                  </Group>

                  <Group justify="space-between">
                    <Text fw={500}>Email:</Text>
                    <Text>{user?.email}</Text>
                  </Group>

                  <Group justify="space-between">
                    <Text fw={500}>Organization:</Text>
                    <Text>{user?.current_organization?.name || "No organization"}</Text>
                  </Group>

                  <Group justify="space-between" align="flex-start">
                    <Text fw={500}>Teams & Roles:</Text>
                    <Stack gap="xs" align="flex-end">
                      {user?.teams && user.teams.length > 0 ? (
                        user.teams.map(mapping => (
                          <Group key={mapping.team.id} gap="xs" justify="flex-end">
                            <CopyButton value={mapping.team.id}>
                              {({ copied, copy }) => (
                                <Tooltip label={copied ? "Copied!" : "Copy Team ID"} withArrow>
                                  <ActionIcon
                                    variant="subtle"
                                    color={copied ? "green" : "gray"}
                                    size="sm"
                                    onClick={copy}
                                  >
                                    <Icon name={copied ? "check" : "copy"} size="xs" />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </CopyButton>
                            <Badge variant="light" size="sm">
                              {mapping.team.name}
                            </Badge>
                            <Badge variant="filled" size="sm" color={getRoleColor(mapping.role)}>
                              {getRoleDisplayName(mapping.role)}
                            </Badge>
                          </Group>
                        ))
                      ) : (
                        <Text size="sm" c="dimmed">
                          No teams
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </Stack>
              </Stack>
            </Card>

            <Divider />

            <APIKeys />

            <Button
              color="red"
              variant="outline"
              size="md"
              onClick={() => {
                void clearApplicationState().then(() => {
                  void logout({ logoutParams: { returnTo: window.location.origin } });
                });
              }}
            >
              Logout
            </Button>
          </Stack>
        </Center>
      </Container>
    </Page>
  );
}
